import {
  COMPANY_SANCTION_CATEGORIES,
  COMPANY_SANCTION_CATEGORY_SEVERITY,
  compareSeverity,
} from '../../consts';
import type { CompanySanctionCategory } from '../../consts';

/**
 * Raw vendor match shape (e.g. AsiaVerify response item). Category strings
 * live in three nested locations on `entity` — all optional because vendors
 * vary in which they populate per match.
 */
export type CompanySanctionMatchInput = {
  entity?: {
    categories?: string[];
    category?: string;
    sources?: Array<{ categories?: string[] }>;
  };
};

const COMPANY_CATEGORY_MATCHERS: Record<CompanySanctionCategory, (raw: string) => boolean> = {
  Terrorism: raw => raw.includes('terror'),
  Sanctions: raw => raw.includes('sanctions'),
  'Adverse Media': raw => raw.includes('reputational risk exposure'),
  PEP: raw => raw.includes('pep'),
  'State-Owned Enterprise': raw => raw.includes('soe') || raw.includes('state owned'),
};

const collectCategoryStrings = (match: CompanySanctionMatchInput): string[] => {
  const entity = match.entity;

  if (!entity) {
    return [];
  }

  const strings: string[] = [];

  strings.push(...(entity.categories ?? []));

  if (typeof entity.category === 'string') {
    strings.push(entity.category);
  }

  for (const source of entity.sources ?? []) {
    strings.push(...(source.categories ?? []));
  }

  return strings;
};

/**
 * Extracts canonical company-sanction categories from a vendor match by
 * substring-matching the raw category strings. Result is sorted by severity
 * (critical first), preserving the order defined in COMPANY_SANCTION_CATEGORIES
 * within the same severity tier.
 *
 * NOTE: matchers must stay in sync with the SQL `like_regex` patterns used in
 * the migration that backfills `company_sanctions_checks.categories`. Hot-path
 * queries no longer regex over JSONB — they rely on this function having been
 * called at write time.
 */
export const extractCompanySanctionCategories = (
  match: CompanySanctionMatchInput,
): CompanySanctionCategory[] => {
  const lowerCased = collectCategoryStrings(match)
    .filter((s): s is string => typeof s === 'string')
    .map(s => s.toLowerCase());

  return COMPANY_SANCTION_CATEGORIES.filter(category =>
    lowerCased.some(raw => COMPANY_CATEGORY_MATCHERS[category](raw)),
  ).sort((a, b) =>
    compareSeverity(COMPANY_SANCTION_CATEGORY_SEVERITY[a], COMPANY_SANCTION_CATEGORY_SEVERITY[b]),
  );
};
